import { useState, useEffect } from 'react'
import { Bell, CheckCircle, AlertTriangle, Info, XCircle, Filter, Plus, Pause, Play, Mail } from 'lucide-react'
import api from '../lib/api'
import { format } from 'date-fns'
import toast from 'react-hot-toast'

const Alerts = () => {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('Active')

  useEffect(() => {
    fetchAlerts()
  }, [priorityFilter, statusFilter])

  const fetchAlerts = async () => {
    try {
      const params = {}
      if (priorityFilter !== 'all') params.priority = priorityFilter
      if (statusFilter !== 'all') params.status = statusFilter
      
      const response = await api.get('/alerts', { params })
      setAlerts(response.data)
    } catch (error) {
      console.error('Failed to fetch alerts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAcknowledge = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/acknowledge`)
      fetchAlerts()
    } catch (error) {
      console.error('Failed to acknowledge alert:', error)
    }
  }

  const handleResolve = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/resolve`, { userId: null })
      fetchAlerts()
    } catch (error) {
      console.error('Failed to resolve alert:', error)
    }
  }

  const handlePause = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/pause`, { userId: null })
      fetchAlerts()
    } catch (error) {
      console.error('Failed to pause alert:', error)
    }
  }

  const handleResume = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/resume`, { userId: null })
      fetchAlerts()
    } catch (error) {
      console.error('Failed to resume alert:', error)
    }
  }

  const handleSendEmail = async (alertId) => {
    const email = prompt('Enter recipient email address:')
    if (!email) return
    
    try {
      await api.post(`/alerts/${alertId}/send-email`, {
        recipients: [email]
      })
      toast.success('Alert email sent successfully!')
    } catch (error) {
      console.error('Failed to send email:', error)
      toast.error('Failed to send email')
    }
  }

  const [showAddModal, setShowAddModal] = useState(false)
  const [newAlert, setNewAlert] = useState({
    type: 'Tender',
    priority: 'Medium',
    title: '',
    message: ''
  })

  const handleCreateAlert = async (e) => {
    e.preventDefault()
    try {
      await api.post('/alerts', newAlert)
      setShowAddModal(false)
      setNewAlert({
        type: 'Tender',
        priority: 'Medium',
        title: '',
        message: ''
      })
      fetchAlerts()
      toast.success('Alert created successfully!')
    } catch (error) {
      console.error('Failed to create alert:', error)
      toast.error('Failed to create alert')
    }
  }

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'bg-red-100 text-red-700 border-red-300',
      'High': 'bg-orange-100 text-orange-700 border-orange-300',
      'Medium': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'Low': 'bg-blue-100 text-blue-700 border-blue-300'
    }
    return colors[priority] || 'bg-gray-100 text-gray-700 border-gray-300'
  }

  const getPriorityIcon = (priority) => {
    if (priority === 'Critical') return <AlertTriangle className="w-5 h-5" />
    if (priority === 'High') return <AlertTriangle className="w-5 h-5" />
    return <Info className="w-5 h-5" />
  }

  const getCategoryIcon = (category) => {
    const icons = {
      'Warning': <AlertTriangle className="w-5 h-5 text-yellow-600" />,
      'Error': <XCircle className="w-5 h-5 text-red-600" />,
      'Success': <CheckCircle className="w-5 h-5 text-green-600" />,
      'Info': <Info className="w-5 h-5 text-blue-600" />
    }
    return icons[category] || <Bell className="w-5 h-5 text-gray-600" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    )
  }

  const criticalAlerts = alerts.filter(a => a.priority === 'Critical' && a.status === 'Active')
  const highAlerts = alerts.filter(a => a.priority === 'High' && a.status === 'Active')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Alerts</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Monitor important notifications and actions</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Alert
        </button>
      </div>

      {/* Alert Summary */}
      {(criticalAlerts.length > 0 || highAlerts.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {criticalAlerts.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-red-900 dark:text-red-300">
                    {criticalAlerts.length} Critical Alert{criticalAlerts.length > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400">Immediate action required</p>
                </div>
              </div>
            </div>
          )}
          {highAlerts.length > 0 && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border-2 border-orange-200 dark:border-orange-800 rounded-xl p-4">
              <div className="flex items-center">
                <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-300">
                    {highAlerts.length} High Priority Alert{highAlerts.length > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-orange-700 dark:text-orange-400">Attention needed</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Acknowledged">Acknowledged</option>
            <option value="Resolved">Resolved</option>
            <option value="Dismissed">Dismissed</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Priority</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {alerts.map((alert) => (
          <div
            key={alert._id}
            className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 p-6 hover:shadow-md transition-shadow ${getPriorityColor(alert.priority)}`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-1">
                {getCategoryIcon(alert.category)}
              </div>
              
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{alert.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(alert.priority)}`}>
                        {alert.priority}
                      </span>
                      {alert.aiGenerated && (
                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                          AI Generated
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{alert.type}</p>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {format(new Date(alert.triggerDate), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>

                <p className="text-gray-700 dark:text-gray-300 mb-4">{alert.message}</p>

                {alert.dueDate && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Due:</span> {format(new Date(alert.dueDate), 'MMM dd, yyyy')}
                  </div>
                )}

                {alert.actions && alert.actions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {alert.actions.map((action, index) => (
                      <a
                        key={index}
                        href={action.url}
                        className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                      >
                        {action.label}
                      </a>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 flex-wrap">
                  {alert.status === 'Active' && (
                    <>
                      <button
                        onClick={() => handleAcknowledge(alert._id)}
                        className="px-3 py-1 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/50"
                      >
                        Acknowledge
                      </button>
                      <button
                        onClick={() => handlePause(alert._id)}
                        className="px-3 py-1 text-sm bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded hover:bg-yellow-100 dark:hover:bg-yellow-900/50 flex items-center gap-1"
                      >
                        <Pause className="w-3 h-3" />
                        Pause
                      </button>
                    </>
                  )}
                  {alert.status === 'Paused' && (
                    <button
                      onClick={() => handleResume(alert._id)}
                      className="px-3 py-1 text-sm bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded hover:bg-green-100 dark:hover:bg-green-900/50 flex items-center gap-1"
                    >
                      <Play className="w-3 h-3" />
                      Resume
                    </button>
                  )}
                  {alert.status !== 'Resolved' && (
                    <button
                      onClick={() => handleResolve(alert._id)}
                      className="px-3 py-1 text-sm bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded hover:bg-green-100 dark:hover:bg-green-900/50"
                    >
                      Resolve
                    </button>
                  )}
                  <button
                    onClick={() => handleSendEmail(alert._id)}
                    className="px-3 py-1 text-sm bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded hover:bg-purple-100 dark:hover:bg-purple-900/50 flex items-center gap-1"
                  >
                    <Mail className="w-3 h-3" />
                    Send Email
                  </button>
                </div>

                {(alert.status === 'Resolved' || alert.status === 'Dismissed') && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                    {alert.status} on {alert.resolvedAt ? format(new Date(alert.resolvedAt), 'MMM dd, yyyy') : 'N/A'}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {alerts.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No alerts found</p>
            <p className="text-sm text-gray-400 mt-2">All caught up! 🎉</p>
          </div>
        )}
      </div>

      {/* Create Alert Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Create New Alert</h3>
            <form onSubmit={handleCreateAlert} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Alert Title *</label>
                <input
                  type="text"
                  value={newAlert.title}
                  onChange={(e) => setNewAlert({...newAlert, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g., New tender matching criteria"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message *</label>
                <textarea
                  value={newAlert.message}
                  onChange={(e) => setNewAlert({...newAlert, message: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Describe the alert..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select
                  value={newAlert.type}
                  onChange={(e) => setNewAlert({...newAlert, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option>Tender</option>
                  <option>Proposal</option>
                  <option>Lead</option>
                  <option>Event</option>
                  <option>System</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                <select
                  value={newAlert.priority}
                  onChange={(e) => setNewAlert({...newAlert, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Create Alert
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Alerts
